import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { Download } from 'lucide-react';
import { AdminButton, AdminCard, AdminSectionTitle } from '../../ui';
import { CreatorIncome, exportCreatorIncome, getCreatorIncome } from '../../../../api/payment/brand-ledger.api';
import { buildIncomeWorkbook, downloadWorkbook, incomeFileName } from '../../../../utils/brand-ledger-export';
import { apiErrorMessage } from '../../../../utils/api-error-message';

const FIRST_YEAR = 2025;
const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

/** Bảng tổng thu nhập từng creator trên sàn hợp tác theo năm/tháng giờ Việt Nam (spec §5), xuất Excel. */
const CreatorIncomeSection: React.FC = () => {
  const thisYear = dayjs().year();
  const years = Array.from({ length: Math.max(1, thisYear - FIRST_YEAR + 1) }, (_, i) => thisYear - i);
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState<number | ''>(dayjs().month() + 1);
  const [data, setData] = useState<CreatorIncome | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getCreatorIncome(year, month || undefined));
    } catch (e) {
      setData(null);
      toast.error(apiErrorMessage(e, 'Không tải được bảng thu nhập creator.'));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await exportCreatorIncome(year, month || undefined);
      downloadWorkbook(buildIncomeWorkbook(all), incomeFileName(year, month || null));
      toast.success(`Đã xuất ${(all.items ?? []).length.toLocaleString('vi-VN')} creator.`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Không xuất được bảng thu nhập creator.'));
    } finally {
      setExporting(false);
    }
  };

  const sum = (pick: (i: CreatorIncome['items'][number]) => number) => (data?.items ?? []).reduce((s, i) => s + pick(i), 0);

  const totalCell: React.CSSProperties = { background: 'var(--adm-bg)', fontWeight: 700, color: 'var(--adm-heading)', borderBottom: 0 };

  return (
    <section className="adm-stack">
      <AdminSectionTitle
        title="Thu nhập creator trên sàn hợp tác"
        extra={
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
            <label className="adm-field">
              Năm
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="adm-select" style={{ width: 120 }}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="adm-field">
              Tháng
              <select value={month} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')} className="adm-select" style={{ width: 140 }}>
                <option value="">Cả năm</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    Tháng {m}
                  </option>
                ))}
              </select>
            </label>
            <AdminButton variant="soft" icon={<Download size={18} />} onClick={handleExport} disabled={exporting}>
              {exporting ? 'Đang xuất…' : 'Xuất Excel'}
            </AdminButton>
          </div>
        }
      />

      <AdminCard flush>
        {loading && !data ? (
          <p className="adm-muted" style={{ margin: 0, padding: 20, fontSize: 14 }}>Đang tải…</p>
        ) : !data || (data.items ?? []).length === 0 ? (
          <p className="adm-muted" style={{ margin: 0, padding: 20, fontSize: 14 }}>Chưa có creator nào nhận tiền trong kỳ này.</p>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Họ tên theo CCCD</th>
                  <th>Tên hiển thị</th>
                  <th>Mã số thuế hoặc CCCD</th>
                  <th className="adm-num">Tổng giá trị</th>
                  <th className="adm-num">Phí sàn</th>
                  <th className="adm-num">Thực nhận</th>
                  <th className="adm-num">Số đơn</th>
                  <th className="adm-num">Bồi thường</th>
                </tr>
              </thead>
              <tbody>
                {(data.items ?? []).map((i) => (
                  <tr key={i.creatorId}>
                    <td>{i.identityName ?? ''}</td>
                    <td>{i.displayName}</td>
                    <td>{i.taxOrId ?? ''}</td>
                    <td className="adm-num">{fmtVnd(i.grossVnd)}</td>
                    <td className="adm-num">{fmtVnd(i.feeVnd)}</td>
                    <td className="adm-num">{fmtVnd(i.netVnd)}</td>
                    <td className="adm-num">{i.dealCount}</td>
                    <td className="adm-num">{fmtVnd(i.compensationVnd)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={totalCell} colSpan={3}>
                    Tổng
                  </td>
                  <td className="adm-num" style={totalCell}>{fmtVnd(sum((i) => i.grossVnd))}</td>
                  <td className="adm-num" style={totalCell}>{fmtVnd(sum((i) => i.feeVnd))}</td>
                  <td className="adm-num" style={totalCell}>{fmtVnd(sum((i) => i.netVnd))}</td>
                  <td className="adm-num" style={totalCell}>{sum((i) => i.dealCount)}</td>
                  <td className="adm-num" style={totalCell}>{fmtVnd(sum((i) => i.compensationVnd))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </section>
  );
};

export default CreatorIncomeSection;
